<template>
  <div v-show="isNavigationBlocked" data-testid="nav-block-loader" class="nav-block-loader">
    <v-progress-circular indeterminate size="48" color="primary" />
  </div>
  <v-theme-provider v-if="isAppReady" v-show="!isNavigationBlocked" :theme="appTheme">
    <cs-app
      class="stb-app"
      :show-header="false"
      :side-nav-width="sideNavWidth"
      :version="version"
      home-route="/excursions"
    >
      <template #right-side-nav>
        <hx-homebar-horizontal
          v-if="showHeader"
          id="stb-homebar-horizontal"
          :order="-1"
          avatar-type="initial"
          :name="homebarName"
          :email="userEmail || ''"
          :product-logo="stbProductLogo"
          product-text=""
          :show-site-settings="showSiteSettings"
          :show-beta-tester="false"
          :loading="isInitClientConfigInProgress"
          @help-click="registerSupportLinkClickEvent"
          @profile-header-click="userInfoDisplayed = true"
          @site-settings-click="handleSettingsAction"
          @feedback-click="redirect('/feedback')"
          @about-click="openAboutIdeagen"
          @sign-out-click="logout"
        />
        <!--
          Implicit DOM contract: `class="client-logo"` and `<img id="clientLogo">` are read
          imperatively by `setLogoImgUrl` in src/store/modules/common/actions.js. Renaming
          either kills the school-logo render with no error. The action gracefully no-ops on
          routes where this banner is not mounted (see clientLogoBannerDisplayed below) —
          intentional per CSSES-976 route-gating.
        -->
        <v-app-bar
          v-if="clientLogoBannerDisplayed"
          id="stb-client-logo-banner"
          class="client-logo-banner client-logo"
          data-testid="client-logo-banner"
          :order="-1"
          :height="64"
          color="white"
          flat
        >
          <img id="clientLogo" alt="School logo" class="client-logo-banner__img" />
        </v-app-bar>
        <app-side-nav v-if="showSideNav" @view-user-info="userInfoDisplayed = true"></app-side-nav>
      </template>
      <template #router-view>
        <div
          v-if="crossTenantSessionEnabled"
          class="cross-tenant-session-banner cross-tenant-session-color"
        >
          Cross-tenant session active. To log out, please use the main site.
        </div>
        <v-slide-y-transition>
          <div :class="contentHtmlClassConcat">
            <maintenance-mode
              v-if="isClientInMaintenanceMode"
              :is-system-admin="maintenanceModeBypassEnabled"
              :is-maintenance-mode="isClientInMaintenanceMode"
            />
            <router-view v-if="!showMaintenancePage && isAuthReady" v-slot="{ Component }">
              <component :is="Component" />
            </router-view>
          </div>
        </v-slide-y-transition>
      </template>

      <template #footer>
        <div :class="footerClass">
          <div v-if="isDemoEnvironment" class="demo-note">
            PLEASE NOTE: This is a demo site only, please do not enter any personal information.
            Information entered here may be deleted at any time.
          </div>
          <div class="d-flex flex-column align-center justify-center">
            <v-img :src="policyLogicLogo" width="200px" height="55px" class="img" />
            <span>
              &copy;{{ getCopyrightYearRange() }} Ideagen Policy Logic (v{{ version }}). All rights
              reserved.
            </span>
          </div>
        </div>
      </template>
    </cs-app>
    <cs-form-dialog
      id="dlg-response-with-error"
      v-model="isResponseWithError"
      body-id="dlg-body-response-with-error"
      heading="Server Error"
      :primary-action="{
        label: 'Close',
        eventName: 'primary-click'
      }"
      @primary-click="closeErrorMessageDialogBtnClick"
    >
      <template #cs-form-dialog-content>
        <template v-if="isMaintenanceModeError">
          <p>
            This site is currently in maintenance mode. <br />Changes are currently not allowed.
            <br />Please try again later.
          </p>
        </template>
        <template v-else-if="isExcursionTypeNotSupportedError">
          {{ excursionTypeNotSupportedErrorMessage }}
        </template>
        <template v-else>
          There seems to be an issue with the server, please try again later or log a support
          request.
        </template>
        <div v-if="!isExcursionTypeNotSupportedError" v-safe-html="debugReferenceMessage"></div>
      </template>
    </cs-form-dialog>
    <cs-form-dialog
      id="dlg-rbacl-not-configured"
      v-model="isRbaclNotConfigured"
      body-id="dlg-body-rbacl-not-configured"
      heading="Access Not Configured"
      persistent
      :primary-action="{
        label: 'OK',
        eventName: 'primary-click'
      }"
      @primary-click="logout"
    >
      <template #cs-form-dialog-content>
        Your account is not configured for access to this site. Please contact your administrator.
      </template>
    </cs-form-dialog>
    <cs-form-dialog
      id="dlg-user-info"
      v-model="userInfoDisplayed"
      body-id="dlg-body-user-info"
      heading="User Information"
      :primary-action="{
        label: 'Close',
        eventName: 'primary-click'
      }"
      @primary-click="userInfoDisplayed = false"
    >
      <template #cs-form-dialog-content>
        <div v-safe-html="userInfo"></div>
      </template>
    </cs-form-dialog>
    <!--
      Cross-tenant Sign Out hover tooltip. The avatar menu's red Sign Out
      `v-list-item` is teleported into the DOM by Vuetify only when the menu
      opens, so a CSS-selector activator on this v-tooltip would query null
      at mount and never fire. Instead, signOutMenuItemEl is bound by the
      MutationObserver in mounted() — when CSDS renders the menu item,
      Vuetify re-attaches this tooltip's activator to the live DOM node.
    -->
    <v-tooltip
      v-if="crossTenantSessionEnabled && signOutMenuItemEl"
      :activator="signOutMenuItemEl"
      location="left"
      :open-delay="500"
      :close-delay="200"
      max-width="274"
      content-class="stb-tooltip pinch-right"
    >
      Sign out from the root app to end your cross-tenant session.
    </v-tooltip>
  </v-theme-provider>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { HxHomebarHorizontal } from '@complispace/cs-design-system';
import sessionManager from '@/store/modules/session/sessionManager';
import * as types from './store/mutationTypes';
import defaultClientLogo from './assets/image/YourSchoolLogo.png';
import trackableEvent from './lib/const/trackableEvent';
import policyLogicLogo from './assets/image/PolicyLogicHorizontalLogo.svg';
import stbProductLogo from './assets/image/STBHorizontalWhite.svg';
import {
  populateErrorMessage,
  extractExcursionTypeNotSupportedMessage,
  EXCURSION_TYPE_MARKER,
  NOT_SUPPORTED_MARKER
} from './lib/errorMessages';
import AppSideNav from './components/AppSideNav';
import MaintenanceMode from './components/MaintenanceMode';

import packageParam from '../package.json';
import errorCaseSid from './lib/const/errorCaseSid';

export default {
  name: 'App',

  components: {
    AppSideNav,
    HxHomebarHorizontal,
    MaintenanceMode
  },

  data() {
    return {
      appTheme: 'ce',
      fixed: true,
      excursionTypes: ['Type1', 'Type2'],
      riskCategories: [
        'High-Risk Student Medical Conditions',
        'Transport',
        'Venues',
        'Food and Beverage',
        'Activity'
      ],
      riskClasses: ['Swimming', 'Boarding'],
      excursionActive: false,
      routerReady: false,
      policyLogicLogo,
      stbProductLogo,
      defaultClientLogo,
      userInfoDisplayed: false,
      // Live reference to the cross-tenant Sign Out menu item DOM node. Bound
      // by signOutMenuItemObserver in mounted() when CSDS teleports the
      // avatar menu open; v-tooltip uses it as the :activator.
      signOutMenuItemEl: null
    };
  },

  computed: {
    ...mapState({
      userProfile: (state) => state.authorisation.userProfile,
      userDisplayName: (state) => state.common.userDisplayName,
      userEmail: (state) => state.common.userEmail,
      isInitClientConfigInProgress: (state) => state.authorisation.isInitClientConfigInProgress,
      usersTabDisplayed: (state) => state.common.usersTabDisplayed,
      clientsTabDisplayed: (state) => state.common.clientsTabDisplayed,
      clientSiteSettingsButtonDiplayed: (state) => state.common.clientSiteSettingsButtonDiplayed,
      isClientInMaintenanceMode: (state) => state.common.isClientInMaintenanceMode,
      maintenanceModeBypassEnabled: (state) => state.common.maintenanceModeBypassEnabled
    }),
    ...mapGetters({
      isLoggedIn: 'authorisation/isLoggedIn',
      auth0Enabled: 'authorisation/auth0Enabled',
      silentAxiosError: 'app/silentAxiosError',
      isHubEnabled: 'authorisation/hubEnabled',
      crossTenantSessionEnabled: 'authorisation/crossTenantSessionEnabled'
    }),
    userInfo() {
      return `<div class="profile-tooltip-content">${JSON.stringify(
        this.userProfile,
        null,
        2
      )}</div>`;
    },
    homebarName() {
      // CSSES-976: the Helix homebar avatar (CSDS HxAvatarBase) renders nothing
      // when `name` is empty, and the Sign Out menu uses that avatar as its only
      // visible trigger. userDisplayName/userEmail come from getClientSetups: in
      // the common "unauthorized to view" case setups still returns the identity,
      // so name and email show normally. But when setups returns no data (bad
      // token, or before it resolves on first load) both are empty and the avatar
      // — and the only logout path — disappears. Fall back to email, then a
      // generic label, so the avatar and its Sign Out menu always render. This
      // does not hide the email: the homebar's :email still binds userEmail.
      return this.userDisplayName || this.userEmail || 'Account';
    },
    showSiteSettings() {
      const roles = [this.userProfile?.integratedAclRole, this.userProfile?.mixedAclRole];
      return !roles.includes('client-user');
    },
    clientLogoBannerDisplayed() {
      const bannerRoutes = ['SafeTripBuilderNewRecord', 'SafeTripBuilderExistingRecord'];
      return (
        this.showHeader && !this.showMaintenancePage && bannerRoutes.includes(this.$route.name)
      );
    },
    sideNavWidth() {
      return 0;
    },

    showSideNav() {
      return (
        this.isLoggedIn &&
        !this.vendorIntegrationWindowViewEnabled &&
        !this.isOnAhaFeedbackLoginPage &&
        !this.showMaintenancePage
      );
    },

    version() {
      const { bundleVersion } = packageParam;
      const uiVersion = `${packageParam.version}${packageParam.hotfixIndicator}`;
      const dashApiVersion = this.$store.state.app.apiVersion
        ? `-${this.$store.state.app.apiVersion}`
        : '';

      return `${bundleVersion}#${uiVersion}${dashApiVersion}`;
    },

    isResponseWithError: {
      get() {
        return this.$store.state.common.isResponseWithError && !this.silentAxiosError;
      },
      set(value) {
        this.$store.commit(types.COMMON_SET_IS_RESPONSE_WITH_ERROR, value);
      }
    },
    isRbaclNotConfigured: {
      get() {
        return this.$store.state.common.isRbaclNotConfigured;
      },
      set(value) {
        this.$store.commit(types.COMMON_SET_IS_RBACL_NOT_CONFIGURED, value);
      }
    },
    debugReferenceMessage() {
      let dialogMessage = '';
      dialogMessage = populateErrorMessage({ message: dialogMessage, store: this.$store });
      if (dialogMessage === '') {
        dialogMessage += `<p class="v-card__text debug-error-message mt-3">Unknown Error!</p>`;
      }
      return dialogMessage;
    },
    isMaintenanceModeError() {
      const { message } = this.$store.state.common.apiErrorMessage || {};
      return typeof message === 'string' && message.includes(errorCaseSid.MAINTENANCE_MODE_ENABLED);
    },
    isExcursionTypeNotSupportedError() {
      const { message } = this.$store.state.common.apiErrorMessage || {};
      return (
        typeof message === 'string' &&
        message.includes(EXCURSION_TYPE_MARKER) &&
        message.includes(NOT_SUPPORTED_MARKER)
      );
    },
    excursionTypeNotSupportedErrorMessage() {
      const { message } = this.$store.state.common.apiErrorMessage || {};
      return extractExcursionTypeNotSupportedMessage(message);
    },
    isDemoEnvironment() {
      return this.$app.env.APP_ENV === 'demo';
    },
    contentHtmlClassConcat() {
      if (!this.vendorIntegrationWindowViewEnabled || this.isOnLoginPage) {
        return '';
      }
      let localContentHtmlClassConcat = 'content';

      switch (this.$route.name) {
        case 'ClientAdminLayout':
          localContentHtmlClassConcat += ' full-width-layout';
          break;

        case 'SupportFeedbackLayout':
          localContentHtmlClassConcat += ' full-width-layout iframe-container-layout';
          break;

        default:
          break;
      }

      return localContentHtmlClassConcat;
    },
    showHeader() {
      return (
        this.isLoggedIn &&
        !this.vendorIntegrationWindowViewEnabled &&
        !this.isOnAhaFeedbackLoginPage
      );
    },
    isOnLoginPage() {
      return this.$route.path.toLowerCase() === '/login';
    },
    vendorIntegrationWindowViewEnabled() {
      const { query } = this.$route;
      return Boolean(query.referrer || query['vendor-sid'] || query['oauth-signature']);
    },
    isOnLoginCallbackPath() {
      const path =
        typeof window !== 'undefined' && window.location && window.location.pathname
          ? window.location.pathname
          : (this.$route && this.$route.path) || '';
      return path.toLowerCase().startsWith('/login/callback');
    },
    isAppReady() {
      // Hub tenants: cs-app must mount so CSDS can load the hub config (which sets
      // hub/isHubConfigLoaded). The router guard waits for that flag, so gating cs-app
      // behind routerReady would deadlock. Auth0/plain/cross-tenant keep the gate.
      if (this.isHubEnabled) return true;
      return this.routerReady || this.isOnLoginCallbackPath;
    },
    isOnAhaFeedbackLoginPage() {
      return this.$route.name === 'AhaFeedbackLogin';
    },
    footerClass() {
      return this.isOnLoginPage && !this.auth0Enabled
        ? 'content stb-login-footer ma-0 mt-auto pb-10'
        : 'content stb-auth0-login-footer ma-0 mt-auto';
    },
    showMaintenancePage() {
      return this.isClientInMaintenanceMode && !this.maintenanceModeBypassEnabled;
    },
    isNavigationBlocked() {
      return this.$store.getters['app/isNavigationBlocked'];
    },
    isAuthReady() {
      // For Hub: wait for config loaded
      if (this.$store.state.hub) {
        return this.$store.state.hub.isHubConfigLoaded;
      }
      // For Auth0 and standard: always ready (auth0 resolves in CSApp.created before mount)
      return true;
    },
    isRootClient() {
      return sessionManager.getClientSubdomain() === '';
    }
  },

  watch: {
    async version() {
      if (!this.$store.state.app.apiVersion) {
        await this.$store.dispatch('app/setApiVersion');
      }
    },
    async isInitClientConfigInProgress(newVal, oldVal) {
      if (oldVal && !newVal) {
        const isVendorAuthScheme =
          !!this.$route.query['vendor-sid'] && !!this.$route.query['oauth-signature'];
        if (!this.isLoggedIn || isVendorAuthScheme) return;
        await this.$store.dispatch('authorisation/getUserProfile', this.$app.stbApiAdapter);
      }
    },
    async isLoggedIn(newVal, oldVal) {
      if (newVal && !oldVal) {
        await this.createCrossTenantSession();
      }
    },
    async isOnLoginPage(newVal, oldVal) {
      if (newVal && !oldVal) {
        const { eventPluginCommonAdapter, stbApiAdapter } = this.$app;
        const argHash = {
          queryParam: {},
          eventPluginCommonAdapter,
          stbApiAdapter
        };
        await this.$store.dispatch('common/initClientConfig', argHash);
      }
    },
    crossTenantSessionEnabled: {
      immediate: true,
      handler(value) {
        // CSDS renders the avatar dropdown via a Vuetify overlay teleported to <body>,
        // so a body-level marker class is the only stable anchor for our deep selector
        // in the unscoped <style> block below.
        if (typeof document === 'undefined') return;
        document.body.classList.toggle('cross-tenant-session', !!value);
        // Capture-phase click guard: keeps the avatar menu open when the user
        // clicks the visually disabled Sign Out item. Without this, v-menu's
        // default close-on-content-click fires and the menu collapses before
        // the user can read the disabled-reason tooltip. Capture phase is
        // required because v-menu's close handler is on the overlay root —
        // a bubble-phase listener runs too late.
        if (value) {
          document.addEventListener('click', this.guardCrossTenantSignOutClick, true);
        } else {
          document.removeEventListener('click', this.guardCrossTenantSignOutClick, true);
        }
      }
    }
  },
  async created() {
    await this.$store.dispatch('session/init', this.$app.axiosInstance);
  },
  async mounted() {
    // to ensure the page is reloaded when the user navigates back to the page from the browser history
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        window.location.reload();
      }
    });

    // Watch the body subtree for `.v-list-item.red` (the cross-tenant Sign Out
    // item that CSDS renders inside the teleported avatar menu) appearing or
    // disappearing. When it appears we bind signOutMenuItemEl, which causes
    // v-tooltip to attach to that live DOM node. Querying inside the
    // crossTenantSessionEnabled watcher is too early — the menu isn't open yet.
    if (typeof MutationObserver !== 'undefined') {
      this.signOutMenuItemObserver = new MutationObserver(() => {
        const el = document.querySelector('.v-list-item.red');
        if (el !== this.signOutMenuItemEl) this.signOutMenuItemEl = el;
      });
      this.signOutMenuItemObserver.observe(document.body, { childList: true, subtree: true });
    }

    await this.$store.dispatch('app/setApiVersion');

    await this.$router.isReady();
    this.routerReady = true;

    this.$app.eventPluginCommonAdapter.initPluginBatch();

    if (this.isLoggedIn && this.isRootClient && !this.crossTenantSessionEnabled) {
      await this.createCrossTenantSession();
    }
  },

  beforeUnmount() {
    if (typeof document === 'undefined') return;
    document.body.classList.remove('cross-tenant-session');
    document.removeEventListener('click', this.guardCrossTenantSignOutClick, true);
    if (this.signOutMenuItemObserver) {
      this.signOutMenuItemObserver.disconnect();
      this.signOutMenuItemObserver = null;
    }
  },

  methods: {
    async createCrossTenantSession() {
      try {
        this.$store.commit('app/setSilentAxiosError', true);
        await this.$store.dispatch('authorisation/initCrossTenantSession');
      } catch (error) {
        console.error('Error creating cross-tenant session', error.message);
      } finally {
        this.$store.commit('app/setSilentAxiosError', false);
      }
    },
    closeErrorMessageDialogBtnClick() {
      this.isResponseWithError = false;
      this.responseWithErrorMessage = '';
    },
    getLinkItemId(linkItem) {
      return `client-admin-layout--menu-item--link-${linkItem}`;
    },
    logout() {
      // In a cross-tenant session, the Sign Out menu item is rendered visually
      // disabled (opacity + not-allowed cursor) and a hover tooltip explains
      // why. We mirror that intent here with a silent early-return so a click
      // is a true no-op — matches the disabled-button contract users expect.
      if (this.crossTenantSessionEnabled) return;
      this.$store.dispatch('authorisation/logout', this.$route.query);
    },
    guardCrossTenantSignOutClick(event) {
      // Only stop clicks that originate inside the red-coloured menu item
      // (CSDS's `color: "red"` is appended verbatim to v-list-item's class
      // list — see HxHomebarHorizontal.manageProfileItems Sign Out entry).
      // stopImmediatePropagation defeats Vuetify's clickOutside helper, which
      // attaches its own document-level listener at the same phase; plain
      // stopPropagation alone leaves the menu free to close itself.
      const target = event.target instanceof Element ? event.target : null;
      if (target && target.closest('.v-list-item.red')) {
        event.stopPropagation();
        event.stopImmediatePropagation();
        event.preventDefault();
      }
    },

    redirect(path) {
      if (this.$route.path !== path) {
        this.$router.push(path);
      }
    },

    handleSettingsAction() {
      const isSystemAdminMenuDisplayed = this.usersTabDisplayed && this.clientsTabDisplayed;
      if (sessionManager.getClientSubdomain() === '' && isSystemAdminMenuDisplayed) {
        this.redirect('/admin/clients');
      } else {
        this.redirect('/admin/clients/users');
      }
    },

    registerSupportLinkClickEvent() {
      const trackableEventBaseArgHash = {
        defaultParams: true
      };

      this.$app.eventPluginCommonAdapter.registerTrackableEvent(
        trackableEvent.SUPPORT_LINK_CLICK,
        trackableEventBaseArgHash
      );
    },

    resetStoreState() {
      this.$store.commit(types.COMMON_SET_EXCURSION_TYPE, '');
      const excludedAttributeList = [
        'allProviders',
        'allRisks',
        'apiDataDefaultJurisdiction',
        'apiParam',
        'excursionDc',
        'excursionAdditionalInformationEnabled',
        'logoAttachmentUrl',
        'vendorExcursionSidInputEnabled',
        'clientUsersTabDisplayed',
        'usersTabDisplayed',
        'clientsTabDisplayed',
        'clientSiteSettingsButtonDiplayed',
        'clientSelfGroupManagementDisplayed',
        'vendorGroupIdDisplayEnabled',
        'publicSchoolFeatureSetEnabled',
        'riskCategoryManagementMenuItemDisplayed',
        'operationManagementMenuItemDisplayed',
        'studentManagementSubjectManagementDisplayed'
      ];
      const argHash = {
        excludedAttributeList,
        storeModuleName: ''
      };
      this.$store.commit(types.COMMON_RESET_STORE_MODULE_STATE, argHash);
      window.location.reload();
    },
    getCopyrightYearRange() {
      const startYear = '2018';
      return `${startYear}-${new Date().getFullYear().toString()}`;
    },
    openAboutIdeagen() {
      window.open('https://www.ideagen.com/company', '_blank', 'noopener');
    }
  }
};
</script>
<style>
.nav-block-loader {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  z-index: 9999;
}
.admin-management-container {
  margin: 50px;
}
.debug-error-message {
  color: red;
  margin: 0px 0px !important;
  padding: 0px 0px !important;
}
.profile-tooltip-content {
  max-width: 420px;
  padding-top: 2px;
  padding-left: 2px;
  padding-right: 2px;
  word-wrap: break-word;
  clear: right;
  white-space: pre;
  line-height: 1;
}
.stb-login-footer {
  min-width: 100%;
  background-color: #f5f5f5;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.stb-app .cs-footer {
  padding: 0px;
}
/*
  Intentionally unscoped: the rules below override cs-design-system internals (HxHomebarHorizontal
  and the school-logo banner) which render outside this SFC's scoped boundary. Scoped + :deep()
  cannot reliably penetrate a third-party component's `data-v-*` attribute tree. ID-prefixed
  selectors beat the bundle's `[data-v-67e5e8f8]` rules on specificity without resorting to
  !important. Do NOT move these into <style scoped>.
*/
.hx-homebar-horizontal__product-logo-label::before {
  content: '';
  display: block;
  width: 28px;
  height: 32px;
  margin-right: 12px;
  background-image: url('./assets/image/STB.svg');
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  flex-shrink: 0;
}
#stb-client-logo-banner .v-toolbar__content {
  padding: 16px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
/* 20rem (320px) aligns with the CsNavbar width set by CSSES-977 so the homebar prepend column,
   school-logo banner, and side nav share the same left-edge gutter per Figma STB-Admin-Expanded.
   If CSSES-977's nav width changes, change both here. */
#stb-homebar-horizontal .v-toolbar__prepend {
  width: 20rem;
}
#stb-homebar-horizontal .v-toolbar__append {
  width: calc(100% - 20rem);
}
/*
  Cross-tenant Sign Out is visually disabled. The tooltip and click guard
  are wired in script-side via a MutationObserver (signOutMenuItemEl ref +
  v-tooltip below) and a capture-phase document click listener
  (guardCrossTenantSignOutClick). CSS here is intentionally just the
  visual-disabled cue.
*/
body.cross-tenant-session .v-list-item.red {
  opacity: 0.4;
  cursor: not-allowed;
}

/*
  STB-side tooltip visual contract — mirrors CSDS HxTooltip pixel-for-pixel so
  the workaround tooltips in this app (AppSideNav primary-action/footer-left
  in the collapsed CsNavbar, and the cross-tenant Sign Out menu item) look
  identical to CSDS's native menu-item tooltip. Selector starts with
  `.v-tooltip > .v-overlay__content.stb-tooltip` to beat Vuetify's default
  `.v-tooltip > .v-overlay__content` rule (0-3-0 vs 0-2-0 specificity).
  Apply `pinch-left` when the tooltip sits to the RIGHT of the activator,
  `pinch-right` when it sits to the LEFT.
*/
.v-tooltip > .v-overlay__content.stb-tooltip {
  padding: 4px 8px;
  border-radius: 6px;
  background: #2f4051;
  color: #ffffff;
  max-width: 274px;
  font-family: 'Gilroy Medium', Verdana, sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 18px;
}
.v-tooltip > .v-overlay__content.stb-tooltip.pinch-left::before {
  content: '';
  position: absolute;
  border-width: 5px 7px;
  border-style: solid;
  border-color: transparent #2f4051 transparent transparent;
  right: 100%;
  top: 50%;
  transform: translateY(-50%);
}
.v-tooltip > .v-overlay__content.stb-tooltip.pinch-right::before {
  content: '';
  position: absolute;
  border-width: 5px 7px;
  border-style: solid;
  border-color: transparent transparent transparent #2f4051;
  left: 100%;
  top: 50%;
  transform: translateY(-50%);
}
.stb-auth0-login-footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 100%;
}
</style>
<style scoped>
.demo-note {
  padding: 10px;
}
:deep(#cs-side-nav) {
  background-color: rgb(var(--v-theme-black-lighten-3));
  color: #ffffff;
}
.client-logo-banner__img {
  height: 32px;
  max-height: 32px;
  max-width: 220px;
  width: auto;
}
.cross-tenant-session-banner {
  padding: 8px 16px;
  font-weight: bold;
  background-color: #fff8e1;
}
.cross-tenant-session-color {
  color: #ffbf00;
}
</style>
